import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Dropdown, ToastContainer } from "react-bootstrap";
import { FaEllipsisV, FaImages } from "react-icons/fa";
import { request } from "../../../utils/api";
import { toast } from "react-toastify";
import { showImage } from "../../../utils";
import { AbilityContext } from "../../../context/AbilityContext";
import { AuthContext } from "../../../auth/AuthContext";
import ViewUserModal from "../modal/ViewUserModal";
import DeleteModal from "../modal/DeleteModal";
import Loading from "../utilComponent/Loading";
import Pagination from "../utilComponent/Pagination";

const ProductList = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const ability = useContext(AbilityContext);

  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [editingQty, setEditingQty] = useState(null);
  const [qtyValue, setQtyValue] = useState("");
  const [qtyLoading, setQtyLoading] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const isAdminAndSuperAdmin = user?.role_key === "super_admin" || user?.role_key === "admin";

  const ImageGalleryModal = ({ show, images, onClose }) => {
    if (!show) return null;

    const downloadImage = (url, filename = "image.jpg") => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          const blobUrl = URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(blobUrl);
        }, "image/jpeg");
      };

      img.onerror = () => {
        console.error("Could not load the image for download.");
      };

      img.src = url;
    };

    return (
      <div
        className="modal fade show d-block"
        style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      >
        <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Product Images</h5>
              <button className="btn-close" onClick={onClose}></button>
            </div>

            <div className="modal-body">
              <div className="row g-3">
                {images.map((img, index) => {
                  const imageUrl = showImage(img.url);

                  return (
                    <div className="col-md-6 col-lg-4" key={index}>
                      <div className="position-relative">
                        <img
                          src={imageUrl}
                          alt=""
                          className="img-fluid rounded shadow-sm"
                          style={{
                            objectFit: "cover",
                            width: "100%",
                            height: "250px",
                          }}
                        />

                        <button
                          className="btn btn-sm btn-dark position-absolute bottom-0 end-0 m-2"
                          onClick={() =>
                            downloadImage(
                              imageUrl,
                              `product-image-${index + 1}.jpg`
                            )
                          }
                        >
                          ⬇ Download
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };


  const getProductList = async (page = 1) => {
    try {
      setLoading(true);

      const response = await request({
        url: `products?page=${page}&per_page=${pagination.per_page}`,
        method: "GET",
      });

      if (response.status && response.data) {
        setProducts(
          response.data.items.map((item) => ({
            id: item.id,
            sku: item.sku,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            type: item.type,
            warehouse: item.warehouse?.name || "N/A",
            images: item.images || [],
          }))
        );

        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductList(pagination.current_page);
  }, []);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    getProductList(page);
  };

  const deleteProduct = async () => {
    try {
      setLoading(true);

      await request({
        url: `products/${selectedProduct?.id}`,
        method: "DELETE",
      });

      toast.success("Product deleted successfully");
      setShowDeleteModal(false);
      getProductList(pagination.current_page);
    } catch {
      toast.error("Cannot delete product");
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter((item) =>
    Object.values(item).some((v) =>
      String(v).toLowerCase().includes(searchText.toLowerCase())
    )
  );

  const handleQtyUpdate = async (product) => {
    const newQty = parseInt(qtyValue);
    const currentQty = parseInt(product.quantity);

    if (isNaN(newQty)) {
      toast.error("Invalid quantity");
      return;
    }

    if (!isAdminAndSuperAdmin && newQty < currentQty) {
      toast.error(
        `You are not allowed to decrease quantity (Current: ${currentQty})`
      );
      return;
    }


    if (newQty === currentQty) {
      setEditingQty(null);
      setQtyValue("");
      return;
    }

    try {
      setQtyLoading(true);
      const productResponse = await request({
        url: `products/${product.id}`,
        method: "GET",
      });

      const fullProduct = productResponse.data;
      const formData = new FormData();
      formData.append("name", fullProduct.name || "");
      formData.append("quantity", newQty);
      formData.append("type", fullProduct.type || "");
      formData.append("warehouse_id", fullProduct?.warehouse?.id || "");
      if (fullProduct.price) {
        formData.append("price", fullProduct.price);
      }
      await request({
        url: `products/${product.id}`,
        method: "POST",
        data: formData,
      });
      setProducts((prev) =>
        prev.map((item) =>
          item.id === product.id ? { ...item, quantity: newQty } : item
        )
      );

      toast.success("Quantity updated successfully");
      setEditingQty(null);
      setQtyValue("");
    } catch (error) {
      toast.error("Failed to update quantity");
      console.error("Quantity update error:", error);
    } finally {
      setQtyLoading(false);
    }
  };

  const startEditingQty = (product) => {
    setEditingQty(product.id);
    setQtyValue(product.quantity);
  };

  const cancelEditingQty = () => {
    setEditingQty(null);
    setQtyValue("");
  };

  return (
    <>
    <ToastContainer />
      <ImageGalleryModal
        show={showImageModal}
        images={selectedImages}
        onClose={() => setShowImageModal(false)}
      />
      <ViewUserModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        data={selectedProduct}
        fields={[
          { label: "SKU", key: "sku" },
          { label: "Product Name", key: "name" },
          { label: "Price", key: "price" },
          { label: "Quantity", key: "quantity" },
          { label: "Type", key: "type" },
          { label: "Warehouse", key: "warehouse" },
        ]}
        title="Product Details"
        onEdit={(item) => navigate(`/product-update/${item.id}`)}
      />

      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={deleteProduct}
      />

      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <ol className="breadcrumb bg-transparent mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Product</li>
            <li className="breadcrumb-item active">Product List</li>
          </ol>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1">
        <div className="container-fluid">
          <div className="card shadow-sm">
            <div className="card-header">
              <h6 className="card-title m-0">Product List</h6>
            </div>

            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <input
                    className="form-control"
                    placeholder="Search product..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </div>
              </div>

              {loading ? (
                <Loading />
              ) : (
                <div className="row">
                  {filteredProducts.length ? (
                    filteredProducts.map((item) => (
                      <div
                        key={item.id}
                        className="col-xl-4 col-lg-6 mb-4"
                      >
                        <div className="card h-100 shadow-sm">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="d-flex align-items-center">
                                <img
                                  src={showImage(item?.images?.[0]?.url)}
                                  alt={item.name}
                                  width={60}
                                  height={60}
                                  className="rounded me-3"
                                  style={{ objectFit: "cover" }}
                                />

                                <h6 className="fw-bold mb-1">
                                  {item.name}
                                </h6>
                              </div>

                              <Dropdown align="end">
                                <Dropdown.Toggle
                                  variant="light"
                                  className="border-0 p-0"
                                >
                                  <FaEllipsisV />
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                  {ability.can("viewProduct", "all") && <Dropdown.Item
                                    onClick={() => {
                                      setSelectedProduct(item);
                                      setShowViewModal(true);
                                    }}
                                  >
                                    View
                                  </Dropdown.Item>}

                                  {ability.can("updateProduct", "all") && <Dropdown.Item
                                    onClick={() =>
                                      navigate(
                                        `/product-update/${item.id}`
                                      )
                                    }
                                  >
                                    Edit
                                  </Dropdown.Item>}

                                  {ability.can("deleteProduct", "all") && <Dropdown.Item
                                    className="text-danger"
                                    onClick={() => {
                                      setSelectedProduct(item);
                                      setShowDeleteModal(true);
                                    }}
                                  >
                                    Delete
                                  </Dropdown.Item>}
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>

                            <p className="mb-1 text-muted">
                              SKU: {item.sku}
                            </p>
                            <p className="mb-1">
                              💰 Price: <strong>{item.price}</strong>
                            </p>
                            <div className="mb-1 d-flex align-items-center gap-2">
                              <span>📦 Qty:</span>

                              {ability.can("updateProduct", "all") ? (
                                editingQty === item.id ? (
                                  <div className="d-flex align-items-center gap-2 flex-grow-1">
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      style={{ width: "80px" }}
                                      value={qtyValue}
                                      min={isAdminAndSuperAdmin ? 0 : item.quantity}
                                      onChange={(e) => {
                                        const value = e.target.value;

                                        if (value === "") {
                                          setQtyValue("");
                                          return;
                                        }

                                        const numValue = parseInt(value);
                                        if (!isNaN(numValue) && numValue >= 0) {
                                          setQtyValue(value);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") handleQtyUpdate(item);
                                        if (e.key === "Escape") cancelEditingQty();
                                      }}
                                      disabled={qtyLoading}
                                      autoFocus
                                    />

                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() => handleQtyUpdate(item)}
                                      disabled={
                                        qtyLoading ||
                                        qtyValue === "" ||
                                        (!isAdminAndSuperAdmin && parseInt(qtyValue) < item.quantity)
                                      }
                                      title="Save"
                                    >
                                      {qtyLoading ? (
                                        <span className="spinner-border spinner-border-sm" />
                                      ) : (
                                        "✓"
                                      )}
                                    </button>

                                    <button
                                      className="btn btn-sm btn-secondary"
                                      onClick={cancelEditingQty}
                                      disabled={qtyLoading}
                                      title="Cancel"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                ) : (
                                  <div className="d-flex align-items-center gap-2">
                                    <strong>{item.quantity}</strong>
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => startEditingQty(item)}
                                      title="Update Quantity"
                                    >
                                      <i className="fa fa-edit"></i>
                                    </button>
                                  </div>
                                )
                              ) : (
                                <strong>{item.quantity}</strong>
                              )}
                            </div>

                            {item.images.length > 1 && (
                              <button
                                className="btn btn-sm btn-outline-secondary mb-2"
                                onClick={() => {
                                  setSelectedImages(item.images);
                                  setShowImageModal(true);
                                }}
                              >
                                <FaImages className="me-1" />
                                View All Images ({item.images.length})
                              </button>
                            )}

                            <p className="mb-0">
                              🏬 Warehouse:{" "}
                              <strong>{item.warehouse}</strong>
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-5">
                      No products found
                    </div>
                  )}
                </div>
              )}

              {pagination.last_page > 1 && (
                <Pagination
                  currentPage={pagination.current_page}
                  lastPage={pagination.last_page}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductList;
